import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { euro, api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, Banknote, Users, Clock, Check, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const EVENT_TYPES = ["Geburtstag", "Hochzeit", "Firmenfeier", "Jubiläum", "Konfirmation", "Sonstiges"];
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const BookingDialog = ({ open, onOpenChange, service, guests, hours, addonIndexes, requireAuth, blockedDates = [] }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState("stripe");
  const [form, setForm] = useState({
    event_type: "Geburtstag", event_date: "", event_time: "", address: "", city: service.city || "",
    special_requests: "", contact_name: user?.name || "", contact_phone: user?.phone || "",
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = useMemo(() => {
    let t = service.base_price;
    t += Math.max(0, guests - service.base_persons) * (service.extra_person_price || 0);
    t += Math.max(0, hours - service.base_hours) * (service.extra_hour_price || 0);
    (addonIndexes || []).forEach((i) => { t += service.addons[i]?.price || 0; });
    return Math.round(t);
  }, [service, guests, hours, addonIndexes]);

  const submit = async () => {
    if (!user) { requireAuth && requireAuth(); return; }
    if (!form.event_date || !form.address || !form.contact_name || !form.contact_phone) {
      toast.error("Bitte alle Pflichtfelder ausfüllen."); return;
    }
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        service_id: service.id, event_type: form.event_type, event_date: form.event_date,
        event_time: form.event_time || "18:00", guests, hours, address: form.address, city: form.city,
        special_requests: form.special_requests, addon_indexes: addonIndexes || [],
        payment_method: payment, contact_name: form.contact_name, contact_phone: form.contact_phone,
      });
      if (payment === "stripe") {
        const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else {
        toast.success(`Buchung ${booking.reference} angefragt! Barzahlung am Event.`);
        onOpenChange(false);
        window.location.href = "/meine-buchungen";
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Buchung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{step === 1 ? "Event-Details" : "Zahlung"}</DialogTitle>
        </DialogHeader>

        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-center justify-between text-sm">
          <span className="font-semibold text-emerald-900">{service.title}</span>
          <span className="flex items-center gap-3 text-emerald-700 text-xs font-medium">
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{guests}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{hours}h</span>
          </span>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <Label>Anlass</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {EVENT_TYPES.map((t) => (
                  <button key={t} data-testid={`event-type-${t}`}
                    onClick={() => setForm({ ...form, event_type: t })}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${form.event_type === t ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-600"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Datum *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="booking-date" className="w-full flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-background text-sm text-left">
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      {form.event_date ? form.event_date : <span className="text-slate-400">Datum wählen</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={form.event_date ? new Date(form.event_date) : undefined}
                      onSelect={(d) => d && setForm({ ...form, event_date: toISO(d) })}
                      disabled={(date) => { const t = new Date(); t.setHours(0, 0, 0, 0); return date < t || blockedDates.includes(toISO(date)); }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div><Label>Uhrzeit</Label><Input data-testid="booking-time" type="time" value={form.event_time} onChange={upd("event_time")} /></div>
            </div>
            <div><Label>Adresse / Veranstaltungsort *</Label><Input data-testid="booking-address" value={form.address} onChange={upd("address")} placeholder="Straße, Hausnr., PLZ" /></div>
            <div><Label>Stadt</Label><Input data-testid="booking-city" value={form.city} onChange={upd("city")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kontaktname *</Label><Input data-testid="booking-contact-name" value={form.contact_name} onChange={upd("contact_name")} /></div>
              <div><Label>Telefon *</Label><Input data-testid="booking-contact-phone" value={form.contact_phone} onChange={upd("contact_phone")} /></div>
            </div>
            <div><Label>Besondere Wünsche</Label><Textarea data-testid="booking-notes" value={form.special_requests} onChange={upd("special_requests")} placeholder="z.B. vegetarische Optionen, Aufbauzeit …" /></div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="booking-next"
              onClick={() => { if (!form.event_date || !form.address || !form.contact_name || !form.contact_phone) { toast.error("Bitte Pflichtfelder ausfüllen."); return;} setStep(2); }}>
              Weiter zur Zahlung
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <button onClick={() => setPayment("stripe")} data-testid="payment-stripe"
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${payment === "stripe" ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}>
              <CreditCard className="w-6 h-6 text-emerald-600" />
              <div className="flex-1"><div className="font-semibold text-slate-900">Kartenzahlung (Stripe)</div><div className="text-xs text-slate-500">Sofort & sicher online bezahlen</div></div>
              {payment === "stripe" && <Check className="w-5 h-5 text-emerald-600" />}
            </button>
            <button onClick={() => setPayment("cash")} data-testid="payment-cash"
              className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${payment === "cash" ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}>
              <Banknote className="w-6 h-6 text-emerald-600" />
              <div className="flex-1"><div className="font-semibold text-slate-900">Barzahlung am Event</div><div className="text-xs text-slate-500">Bezahle bar direkt bei der Veranstaltung</div></div>
              {payment === "cash" && <Check className="w-5 h-5 text-emerald-600" />}
            </button>

            <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
              <span className="text-slate-600 font-medium">Gesamtpreis</span>
              <span className="font-mono font-extrabold text-2xl text-emerald-700" data-testid="booking-total">{euro(total)}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)} data-testid="booking-back">Zurück</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={loading} onClick={submit} data-testid="booking-confirm">
                {loading ? "Bitte warten…" : payment === "stripe" ? "Jetzt bezahlen" : "Verbindlich buchen"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
